/**
 * SalesPage.jsx
 * Sales velocity report — sortable, filterable table with groupBy toggle.
 * Users can view data by variant (SKU), product, product type, or vendor.
 * Optional period-over-period comparison columns.
 */

import React, { useState, useMemo } from 'react';
import {
  Card,
  DataTable,
  SkeletonBodyText,
  Banner,
  EmptyState,
  Text,
  BlockStack,
  Badge,
  Pagination,
  Select,
  InlineStack,
  TextField,
  Button,
  ButtonGroup,
  Checkbox,
} from '@shopify/polaris';

import { useReport } from '../hooks/useReport';
import { useExport } from '../hooks/useExport';
import { api } from '../api/api';
import {
  formatCurrency,
  formatNumber,
  formatRate,
  formatChange,
  getChangeTone,
  periodLabel,
} from '../utils/formatters';

const ROWS_PER_PAGE = 50;

const SALES_CSV_COLUMNS = [
  { key: 'productTitle', label: 'Product' },
  { key: 'variantTitle', label: 'Variant' },
  { key: 'sku', label: 'SKU' },
  { key: 'productType', label: 'Product Type' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'unitsSold', label: 'Units Sold' },
  { key: 'dailySellRate', label: 'Daily Sell Rate' },
];

const SORT_OPTIONS = [
  { label: 'Revenue (high → low)', value: 'revenue:desc' },
  { label: 'Revenue (low → high)', value: 'revenue:asc' },
  { label: 'Units sold (high → low)', value: 'unitsSold:desc' },
  { label: 'Units sold (low → high)', value: 'unitsSold:asc' },
  { label: 'Daily rate (high → low)', value: 'dailySellRate:desc' },
  { label: 'Product name A → Z', value: 'label:asc' },
];

const GROUP_BY_OPTIONS = [
  { id: 'variant', label: 'SKU' },
  { id: 'product', label: 'Product' },
  { id: 'type', label: 'Type' },
  { id: 'vendor', label: 'Vendor' },
];

/**
 * Return DataTable headings and column content types for a given groupBy mode.
 *
 * @param {string} groupBy
 * @param {boolean} showComparison
 * @returns {{ headings: string[], columnContentTypes: string[] }}
 */
function getColumns(groupBy, showComparison) {
  let headings;
  let columnContentTypes;

  switch (groupBy) {
    case 'product':
      headings = ['Product', 'Type', 'Vendor', 'Revenue', 'Units Sold', 'Variants'];
      columnContentTypes = ['text', 'text', 'text', 'numeric', 'numeric', 'numeric'];
      break;
    case 'type':
      headings = ['Product Type', 'Revenue', 'Units Sold', 'SKU Count', 'Dead Stock', 'Overstocked'];
      columnContentTypes = ['text', 'numeric', 'numeric', 'numeric', 'text', 'text'];
      break;
    case 'vendor':
      headings = ['Vendor', 'Revenue', 'Units Sold', 'SKU Count', 'Dead Stock', 'Overstocked'];
      columnContentTypes = ['text', 'numeric', 'numeric', 'numeric', 'text', 'text'];
      break;
    default: // variant
      headings = ['Product / SKU', 'Type', 'Vendor', 'Revenue', 'Units Sold', 'Sell Rate', 'Status'];
      columnContentTypes = ['text', 'text', 'text', 'numeric', 'numeric', 'text', 'text'];
      break;
  }

  if (showComparison && groupBy === 'variant') {
    headings.push('Rev. Change', 'Units Change');
    columnContentTypes.push('text', 'text');
  }

  return { headings, columnContentTypes };
}

/**
 * Build a DataTable row array for a given groupBy mode.
 *
 * @param {object} row - data row from API
 * @param {string} groupBy
 * @param {boolean} showComparison
 * @param {Map} changeMap
 * @param {boolean} compLoading
 * @returns {Array}
 */
function buildRow(row, groupBy, showComparison, changeMap, compLoading) {
  switch (groupBy) {
    case 'product':
      return [
        <Text fontWeight="semibold" as="span">{row.label}</Text>,
        row.productType || '—',
        row.vendor || '—',
        <Text fontWeight="semibold" as="span">{formatCurrency(row.revenue)}</Text>,
        formatNumber(row.unitsSold),
        formatNumber(row.variantCount),
      ];
    case 'type':
      return [
        <Text fontWeight="semibold" as="span">{row.label}</Text>,
        <Text fontWeight="semibold" as="span">{formatCurrency(row.revenue)}</Text>,
        formatNumber(row.unitsSold),
        formatNumber(row.variantCount),
        row.deadStockCount > 0 ? <Badge tone="critical">{row.deadStockCount}</Badge> : '0',
        row.overstockedCount > 0 ? <Badge tone="attention">{row.overstockedCount}</Badge> : '0',
      ];
    case 'vendor':
      return [
        <Text fontWeight="semibold" as="span">{row.label}</Text>,
        <Text fontWeight="semibold" as="span">{formatCurrency(row.revenue)}</Text>,
        formatNumber(row.unitsSold),
        formatNumber(row.variantCount),
        row.deadStockCount > 0 ? <Badge tone="critical">{row.deadStockCount}</Badge> : '0',
        row.overstockedCount > 0 ? <Badge tone="attention">{row.overstockedCount}</Badge> : '0',
      ];
    default: {
      // variant
      const cells = [
        <BlockStack gap="050">
          <Text variant="bodyMd" fontWeight="semibold" as="span">{row.productTitle}</Text>
          <Text variant="bodySm" tone="subdued" as="span">{row.variantTitle} · {row.sku}</Text>
        </BlockStack>,
        row.productType,
        row.vendor,
        <Text fontWeight="semibold" as="span">{formatCurrency(row.revenue)}</Text>,
        formatNumber(row.unitsSold),
        formatRate(row.dailySellRate),
        row.unitsSold === 0 ? <Badge tone="critical">No sales</Badge> : null,
      ];

      if (showComparison) {
        const change = changeMap.get(row.variantId);
        if (change && !compLoading) {
          cells.push(
            <Badge tone={getChangeTone(change.revenueChange)}>{formatChange(change.revenueChange)}</Badge>,
            <Badge tone={getChangeTone(change.unitsSoldChange)}>{formatChange(change.unitsSoldChange)}</Badge>,
          );
        } else {
          cells.push('—', '—');
        }
      }

      return cells;
    }
  }
}

/**
 * SalesPage — sales velocity report with groupBy toggle and period comparison.
 *
 * @param {{ period: string, overstockDays: number, filterBar: JSX.Element }} props
 */
export function SalesPage({ period, overstockDays, filterBar }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('revenue:desc');
  const [search, setSearch] = useState('');
  const [productType, setProductType] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const [groupBy, setGroupBy] = useState('variant');

  const [sortBy, sortDir] = sort.split(':');

  const { data, loading, error } = useReport(api.getSales, {
    period,
    overstockDays,
    page,
    limit: ROWS_PER_PAGE,
    sortBy,
    sortDir,
    search,
    productType,
    groupBy,
  });

  const { data: filterOptions } = useReport(api.getFilters, { period });

  // Only fetch comparison data when toggle is on and in variant mode
  const enableComparison = showComparison && groupBy === 'variant';
  const { data: comparisonData, loading: compLoading } = useReport(
    enableComparison ? api.getComparison : () => Promise.resolve(null),
    enableComparison ? { period } : {},
  );

  const changeMap = useMemo(() => {
    const map = new Map();
    if (!comparisonData?.data) return map;
    for (const row of comparisonData.data) {
      map.set(row.variantId, {
        revenueChange: row.revenueChange,
        unitsSoldChange: row.unitsSoldChange,
      });
    }
    return map;
  }, [comparisonData]);

  const csvFilename = `sales-velocity-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
  const { exporting, handleExport } = useExport(
    api.getSales,
    { period, overstockDays, sortBy, sortDir, search, productType, groupBy },
    SALES_CSV_COLUMNS,
    csvFilename,
  );

  const typeOptions = [
    { label: 'All types', value: '' },
    ...(filterOptions?.productTypes?.map((t) => ({ label: t, value: t })) || []),
  ];

  const handleGroupByChange = (newGroupBy) => {
    setGroupBy(newGroupBy);
    setPage(1);
    setSort('revenue:desc');
  };

  const { headings, columnContentTypes } = getColumns(groupBy, enableComparison);

  const rows = (data?.data || []).map((row) =>
    buildRow(row, groupBy, enableComparison, changeMap, compLoading),
  );

  const rowLabel = groupBy === 'variant' ? 'SKUs' : groupBy === 'product' ? 'products' : groupBy === 'type' ? 'types' : 'vendors';
  const hasData = !loading && !error;
  const isEmpty = hasData && rows.length === 0;

  return (
    <BlockStack gap="400">
      <Card>{filterBar}</Card>

      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h2">
              Sales Velocity — {periodLabel(period)}
            </Text>
            <InlineStack gap="300" blockAlign="center">
              {data?.pagination && (
                <Text variant="bodySm" tone="subdued" as="span">
                  {formatNumber(data.pagination.total)} {rowLabel}
                </Text>
              )}
              <Button
                size="slim"
                onClick={handleExport}
                loading={exporting}
                disabled={loading || !data?.data?.length}
              >
                Export CSV
              </Button>
            </InlineStack>
          </InlineStack>

          {/* GroupBy toggle */}
          <InlineStack gap="300" blockAlign="center">
            <Text variant="bodySm" tone="subdued" as="span">Group by:</Text>
            <ButtonGroup variant="segmented">
              {GROUP_BY_OPTIONS.map((opt) => (
                <Button
                  key={opt.id}
                  pressed={groupBy === opt.id}
                  onClick={() => handleGroupByChange(opt.id)}
                  size="slim"
                >
                  {opt.label}
                </Button>
              ))}
            </ButtonGroup>
          </InlineStack>

          {/* Search + filters */}
          <InlineStack gap="300" wrap>
            <div style={{ flex: 1, minWidth: 220 }}>
              <TextField
                label=""
                labelHidden
                placeholder="Search by product, SKU, type…"
                value={search}
                onChange={(v) => { setSearch(v); setPage(1); }}
                clearButton
                onClearButtonClick={() => { setSearch(''); setPage(1); }}
                autoComplete="off"
              />
            </div>
            <div style={{ minWidth: 180 }}>
              <Select
                label=""
                labelHidden
                options={typeOptions}
                value={productType}
                onChange={(v) => { setProductType(v); setPage(1); }}
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <Select
                label=""
                labelHidden
                options={SORT_OPTIONS}
                value={sort}
                onChange={(v) => { setSort(v); setPage(1); }}
              />
            </div>
            {groupBy === 'variant' && (
              <Checkbox
                label="Show comparison"
                checked={showComparison}
                onChange={setShowComparison}
              />
            )}
          </InlineStack>

          {enableComparison && comparisonData?.priorRange && (
            <Text variant="bodySm" tone="subdued" as="p">
              Comparing vs prior period ({comparisonData.priorRange.start?.slice(0, 10)} to {comparisonData.priorRange.end?.slice(0, 10)})
            </Text>
          )}

          {error && (
            <Banner title="Error loading sales data" tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          {loading && <SkeletonBodyText lines={12} />}

          {isEmpty && (
            <EmptyState
              heading="No sales data found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>No products match the current filters. Try adjusting your search or time period.</p>
            </EmptyState>
          )}

          {hasData && rows.length > 0 && (
            <>
              <DataTable
                columnContentTypes={columnContentTypes}
                headings={headings}
                rows={rows}
                footerContent={`Page ${data?.pagination?.page} of ${data?.pagination?.totalPages}`}
              />
              <Pagination
                hasPrevious={page > 1}
                hasNext={page < (data?.pagination?.totalPages || 1)}
                onPrevious={() => setPage((p) => p - 1)}
                onNext={() => setPage((p) => p + 1)}
                label={`Page ${page} of ${data?.pagination?.totalPages || 1}`}
              />
            </>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
